import React, { useState, useEffect } from 'react';
import {
  Bell,
  RefreshCw,
  Search,
  AlertTriangle,
  Eye,
  CheckCircle,
  XCircle,
  Shield,
  Info,
  Settings,
  Lock
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

interface NotificationItem {
   id: number;
   title: string;
   message: string;
   type: 'info' | 'warning' | 'error' | 'success' | 'compliance' | 'system' | 'security';
   priority: 'low' | 'medium' | 'high' | 'critical';
   status: 'unread' | 'read' | 'archived';
   createdAt: string;
   isRead: boolean;
}

const CompanyNotifications: React.FC = () => {
   const [notifications, setNotifications] = useState<NotificationItem[]>([]);
   const [loading, setLoading] = useState(true);
   const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
   const [searchTerm, setSearchTerm] = useState('');

   const [pagination, setPagination] = useState({
      page: 1,
      limit: 20,
      total: 0,
      pages: 0
   });

   useEffect(() => {
     loadNotifications();
   }, [filter]);

   const loadNotifications = async () => {
     try {
       setLoading(true);

       // Build query parameters based on filter
       const params = new URLSearchParams();
       if (filter === 'unread') {
         params.append('status', 'unread');
       } else if (filter === 'read') {
         params.append('status', 'read');
       }

       const response = await fetch(`/api/notification?${params.toString()}`, {
         headers: {
           'Authorization': `Bearer ${localStorage.getItem('token')}`,
           'Content-Type': 'application/json'
         }
       });

       if (!response.ok) {
         throw new Error('Failed to fetch notifications');
       }

       const data = await response.json();

       if (data.success) {
         const transformedNotifications: NotificationItem[] = data.data.map((notification: any) => ({
           id: notification.id,
           title: notification.title,
           message: notification.message,
           type: notification.type,
           priority: notification.priority,
           status: notification.status,
           createdAt: notification.createdAt,
           isRead: notification.isRead
         }));

         setNotifications(transformedNotifications);
          setPagination({
            page: 1,
            limit: transformedNotifications.length,
            total: transformedNotifications.length,
            pages: 1
          });
       } else {
         throw new Error(data.message || 'Failed to load notifications');
       }
     } catch (error) {
       console.error('Failed to load notifications:', error);
       setNotifications([]);
     } finally {
       setLoading(false);
     }
   };

   const markAsRead = async (id: number) => {
     try {
       const response = await fetch(`/api/notification/${id}/read`, {
         method: 'PUT',
         headers: {
           'Authorization': `Bearer ${localStorage.getItem('token')}`,
           'Content-Type': 'application/json'
         }
       });

       if (response.ok) {
         setNotifications(prev =>
           prev.map(n => n.id === id ? { ...n, isRead: true, status: 'read' as const } : n)
         );
       } else {
         console.error('Failed to mark notification as read');
       }
     } catch (error) {
       console.error('Failed to mark notification as read:', error);
     }
   };

   const getTypeIcon = (type: string) => {
     switch (type) {
       case 'success': return <CheckCircle size={16} />;
       case 'warning': return <AlertTriangle size={16} />;
       case 'error': return <XCircle size={16} />;
       case 'compliance': return <Shield size={16} />;
       case 'system': return <Settings size={16} />;
       case 'security': return <Lock size={16} />;
       default: return <Info size={16} />;
     }
   };

   const getTypeColor = (type: string) => {
     switch (type) {
       case 'success': return 'text-green-600 bg-green-100';
       case 'warning': return 'text-yellow-600 bg-yellow-100';
       case 'error': return 'text-red-600 bg-red-100';
       case 'compliance': return 'text-blue-600 bg-blue-100';
       case 'system': return 'text-gray-600 bg-gray-100';
       case 'security': return 'text-purple-600 bg-purple-100';
       default: return 'text-slate-600 bg-slate-100';
     }
   };

   if (loading) {
     return (
       <div className="min-h-screen bg-slate-50 flex items-center justify-center">
         <RefreshCw className="text-blue-600 animate-spin" size={40} />
       </div>
     );
   }

   const unreadCount = notifications.filter(n => !n.isRead).length;
   const readCount = notifications.filter(n => n.isRead).length;
   const criticalCount = notifications.filter(n => n.priority === 'critical').length;

   return (
     <div className="min-h-screen bg-slate-50">
       <div className="max-w-6xl mx-auto px-6 py-6">

         {/* Header */}
         <div className="flex items-center justify-between mb-6">
           <div>
             <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Company Notifications</h1><PageInfoButton /></div>
             <p className="text-slate-500 text-sm">Manage system notifications and alerts</p>
           </div>
           <button
             onClick={loadNotifications}
             className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
           >
             <RefreshCw size={16} />
             Refresh
           </button>
         </div>

         {/* Stats */}
         <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
           {[
             { label: 'Total', value: pagination.total, icon: <Bell size={18} className="text-blue-600" /> },
             { label: 'Unread', value: unreadCount, icon: <Eye size={18} className="text-amber-600" /> },
             { label: 'Read', value: readCount, icon: <CheckCircle size={18} className="text-emerald-600" /> },
             { label: 'Critical', value: criticalCount, icon: <AlertTriangle size={18} className="text-red-600" /> },
           ].map(card => (
             <div key={card.label} className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
               <div className="flex items-center gap-3 mb-1">
                 {card.icon}
                 <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{card.label}</span>
               </div>
               <p className="text-3xl font-bold text-slate-900">{card.value}</p>
             </div>
           ))}
         </div>

         {/* Search and Filters */}
         <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-4">
           <div className="flex items-center gap-3">
             <div className="relative flex-1">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
               <input
                 type="text"
                 placeholder="Search notifications by title or message..."
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 onKeyPress={(e) => e.key === 'Enter' && loadNotifications()}
                 className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
               />
             </div>
             <select
               value={filter}
               onChange={(e) => setFilter(e.target.value as 'all' | 'unread' | 'read')}
               className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-700"
             >
               <option value="all">All Notifications</option>
               <option value="unread">Unread Only</option>
               <option value="read">Read Only</option>
             </select>
             <button
               onClick={loadNotifications}
               className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
             >
               <Search size={16} />
               Search
             </button>
           </div>
         </div>

         {/* Notifications Table */}
         <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
           <table className="w-full text-left border-collapse">
             <thead>
               <tr className="bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                 <th className="pl-5 py-3 w-10"><input type="checkbox" className="rounded border-slate-300" /></th>
                 <th className="px-5 py-3">Notification Details</th>
                 <th className="px-5 py-3">Type</th>
                 <th className="px-5 py-3">Priority</th>
                 <th className="px-5 py-3">Status</th>
                 <th className="px-5 py-3">Created</th>
                 <th className="px-5 py-3 text-right pr-5">Actions</th>
               </tr>
             </thead>
             <tbody className="text-slate-600">
               {notifications.length === 0 ? (
                 <tr>
                   <td colSpan={7} className="py-16">
                     <Bell className="w-10 h-10 text-slate-300 mb-3" />
                     <h3 className="font-semibold text-slate-700">No notifications found</h3>
                     <p className="text-sm text-slate-400 mt-1">
                       {filter === 'all' ? 'No notifications yet.' : `No ${filter} notifications found.`}
                     </p>
                   </td>
                 </tr>
               ) : (
                 notifications.map(notification => (
                   <tr key={notification.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                     <td className="pl-5 py-3 text-left"><input type="checkbox" className="rounded border-slate-300" /></td>
                     <td className="px-5 py-3 text-left">
                       <div className="flex items-center gap-3">
                         <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600 text-xs flex-shrink-0">
                           {notification.title?.[0]?.toUpperCase() || 'N'}
                         </div>
                         <div>
                           <div className="text-sm font-medium text-slate-900">{notification.title}</div>
                           <div className="text-xs text-slate-400">{notification.message.substring(0, 50)}...</div>
                         </div>
                       </div>
                     </td>
                     <td className="px-5 py-3 text-left">
                       <div className="flex items-center gap-2">
                         <div className={`w-7 h-7 rounded-full flex items-center justify-center ${getTypeColor(notification.type)}`}>
                           {getTypeIcon(notification.type)}
                         </div>
                         <span className="text-sm text-slate-700 capitalize">{notification.type}</span>
                       </div>
                     </td>
                     <td className="px-5 py-3 text-left">
                       <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${
                         notification.priority === 'critical' ? 'bg-red-50 text-red-700 border border-red-200' :
                         notification.priority === 'high' ? 'bg-orange-50 text-orange-700 border border-orange-200' :
                         notification.priority === 'medium' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                         'bg-green-50 text-green-700 border border-green-200'
                       }`}>
                         {notification.priority}
                       </span>
                     </td>
                     <td className="px-5 py-3 text-left">
                       <div className="flex items-center gap-2">
                         <div className={`w-2 h-2 rounded-full ${notification.isRead ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                         <span className="text-sm text-slate-700">{notification.isRead ? 'Read' : 'Unread'}</span>
                       </div>
                     </td>
                     <td className="px-5 py-3 text-left text-sm text-slate-500">
                       {new Date(notification.createdAt).toLocaleDateString('en-US', {
                         year: 'numeric',
                         month: 'short',
                         day: 'numeric'
                       })}
                     </td>
                     <td className="px-5 py-3 text-left pr-5">
                       <div className="flex items-center justify-end gap-2">
                         {!notification.isRead && (
                           <button
                             onClick={() => markAsRead(notification.id)}
                             className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded text-[10px] font-bold hover:bg-blue-100 transition-colors flex items-center gap-1"
                             title="Mark as Read"
                           >
                             <CheckCircle size={12} />
                             MARK READ
                           </button>
                         )}
                         <button
                           className="p-1.5 bg-white border border-slate-200 text-slate-500 rounded hover:border-blue-300 hover:text-blue-600 transition-colors"
                           title="View Details"
                         >
                           <Eye size={14} />
                         </button>
                       </div>
                     </td>
                   </tr>
                 ))
               )}
             </tbody>
           </table>

           {/* Footer */}
           <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex items-center justify-between">
             <span className="text-xs text-slate-500">
               Showing <span className="font-semibold text-slate-700">{notifications.length}</span> notification{notifications.length !== 1 ? 's' : ''}
             </span>
             <div className="flex items-center gap-2">
               <span className="text-xs text-slate-500 mr-4">Page {pagination.page} of {pagination.pages || 1}</span>
               <button
                 onClick={() => loadNotifications()}
                 disabled={pagination.page <= 1}
                 className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" strokeWidth="2"/></svg>
               </button>
               <button
                 onClick={() => loadNotifications()}
                 disabled={pagination.page >= (pagination.pages || 1)}
                 className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeWidth="2"/></svg>
               </button>
             </div>
           </div>
         </div>
       </div>
     </div>
   );
};

export default CompanyNotifications;
